import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthRoleService } from './auth-role.service';
import { ExternalAccessGrant } from './external-access.service';
import { decodeJwtClaims } from '../utils/jwt.utils';

export const EXTERNAL_ACCESS_ROUTE = '/external/access';

export type ExternalSessionStatus = 'none' | 'active' | 'expired';
export type ExternalSessionEndReason = 'logout' | 'expired' | 'unauthorized';

export interface ExternalSession {
    role: string;
    maskedEmail: string;
    expiresAt: number;
    /** Lets the user sign in again once the session ends without opening the invitation link again. */
    invitationToken: string;
}

const SESSION_KEY = 'external_access';
/** What a Microsoft user gets from the portal; an external session takes the place of all of it. */
const PROJECT_SESSION_KEYS = [
    'msal_access_token', 'idtoken', 'account', 'projectId', 'guid', 'appId', 'project', 'projectname', 'customer',
    'customername', 'factory', 'factoryname', 'iotestorganization', 'iotestorganizationname', 'iotestproject',
    'iotestprojectname', 'access_token', 'username'
];
const MAX_TIMEOUT = 2 ** 31 - 1;
/** Access token claim -> the key a Microsoft session fills with the same data (the header shows them). */
const CLAIM_SESSION_KEYS: ReadonlyArray<readonly [claim: string, key: string]> = [
    ['name', 'username'],
    ['proj.customerid', 'customer'],
    ['proj.customername', 'customername'],
    ['proj.factoryid', 'factory'],
    ['proj.factoryname', 'factoryname'],
    ['iotest.organizationname', 'iotestorganizationname'],
    ['iotest.projectname', 'iotestprojectname']
];

export function isExternalAccessPath(path: string): boolean {
    return /^\/external(?:[/?#]|$)/i.test(path);
}

/**
 * Session of an external user, kept in the same sessionStorage keys the API requests already read
 * (`access_token`, `iotestproject`, ...), so no service has to know where the access token came from.
 */
@Injectable({ providedIn: 'root' })
export class ExternalSessionService {
    private expiryTimer?: ReturnType<typeof setTimeout>;

    constructor(private readonly router: Router, private readonly authRoleService: AuthRoleService) {
        const session = this.read();
        if (session && session.expiresAt > Date.now()) this.scheduleExpiry(session);
    }

    /** The current session while it is valid. */
    get session(): ExternalSession | null {
        const session = this.read();
        return session && session.expiresAt > Date.now() ? session : null;
    }

    status(): ExternalSessionStatus {
        const session = this.read();
        if (!session) return 'none';
        return session.expiresAt > Date.now() ? 'active' : 'expired';
    }

    isActive(): boolean {
        return this.status() === 'active';
    }

    start(grant: ExternalAccessGrant, login: { maskedEmail: string; invitationToken: string }): void {
        this.clear();
        sessionStorage.setItem('access_token', grant.accessToken);
        sessionStorage.setItem('iotestorganization', grant.organizationId);
        sessionStorage.setItem('iotestproject', grant.projectId);
        // The masked email stays as the user name when the token has no name claim.
        sessionStorage.setItem('username', login.maskedEmail || 'External user');
        const claims = decodeJwtClaims(grant.accessToken);
        for (const [claim, key] of CLAIM_SESSION_KEYS) {
            const value = claims[claim];
            const text = typeof value === 'string' ? value.trim() : '';
            if (text) sessionStorage.setItem(key, text);
        }
        this.authRoleService.setRoles([{ roleId: 0, roleName: grant.role }]);
        const session: ExternalSession = {
            role: grant.role, maskedEmail: login.maskedEmail, expiresAt: grant.expiresAt, invitationToken: login.invitationToken
        };
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
        this.scheduleExpiry(session);
    }

    /** Ends the session and sends the user back to the login flow, which restarts with the same invitation. */
    end(reason: ExternalSessionEndReason): void {
        const session = this.read();
        if (!session) return;
        this.clear();
        const token = reason === 'logout' ? '' : session.invitationToken;
        void this.router.navigate([EXTERNAL_ACCESS_ROUTE], {
            queryParams: token ? { token } : undefined,
            replaceUrl: true,
            state: { reason }
        });
    }

    /** Drops the session without navigating. */
    clear(): void {
        clearTimeout(this.expiryTimer);
        this.expiryTimer = undefined;
        sessionStorage.removeItem(SESSION_KEY);
        PROJECT_SESSION_KEYS.forEach(key => sessionStorage.removeItem(key));
        this.authRoleService.clearRoles();
    }

    private scheduleExpiry(session: ExternalSession): void {
        clearTimeout(this.expiryTimer);
        const delay = Math.max(session.expiresAt - Date.now(), 0);
        if (delay > MAX_TIMEOUT) return;
        this.expiryTimer = setTimeout(() => {
            const current = this.read();
            if (!current) return;
            if (current.expiresAt <= Date.now()) this.end('expired');
            else this.scheduleExpiry(current);
        }, delay);
    }

    private read(): ExternalSession | null {
        try {
            const value = JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? 'null');
            if (value && typeof value.role === 'string' && Number.isFinite(value.expiresAt)) {
                return {
                    role: value.role,
                    maskedEmail: String(value.maskedEmail ?? ''),
                    expiresAt: value.expiresAt,
                    invitationToken: String(value.invitationToken ?? '')
                };
            }
        } catch {
            // A corrupt entry is the same as no session.
        }
        return null;
    }
}
